import { useCallback, useEffect, useState } from "react";
import {
  fetchBreaks,
  fetchTimeCut,
  updateBreaks,
  updateTimeCut,
  fetchPdnsDisplay,
  fetchCompaniesPdns,
  updateCompanyPdns,
  fetchSundayHoursDisplay,
  fetchCompaniesSundayHours,
  updateCompanySundayHours,
} from "../services/timeManagementService";
import UseContext from "./useContext";

const DEFAULT_BREAKS = [
  {
    name: "display_breaks",
    title: "Afficher les pauses",
    checked: false,
    desc: "Les pauses seront incluses en tant que lignes distinctes dans le fichier export.",
  },
  {
    name: "exclude_breaks",
    title: "Exclure les pauses",
    checked: false,
    desc: "Les pauses seront exclues du fichier export.",
  },
  {
    name: "add_breaks_to_hours_worked",
    title: "Ajouter les pauses aux heures travaillées",
    checked: false,
    desc: "Les pauses seront intégrées directement au total des heures travaillées dans le fichier export.",
  },
  {
    name: "add_breaks_to_hours_worked_no_line",
    title: "Ajouter les pauses aux heures travaillées (sans ligne pause)",
    checked: false,
    desc: "Les pauses seront intégrées au total des heures travaillées sans ligne de pause distincte dans le fichier export.",
  },
];

const updateSelectedCompany = (companies, companyId, field, value) =>
  companies.map((company) =>
    company._id === companyId ? { ...company, [field]: value } : company,
  );

const useSettingsTimeManagement = () => {
  const { companySelected } = UseContext();
  const companyId = companySelected?.idCompany;
  const [timeCut, setTimeCut] = useState(0);
  const [breaks, setBreaks] = useState(DEFAULT_BREAKS);
  const [isWaiting, setIsWaiting] = useState(false);
  const [isPdnsDisplay, setIsPdnsDisplay] = useState(false);
  const [companiesPdns, setCompaniesPdns] = useState([]);
  const [isSundayHoursDisplay, setIsSundayHoursDisplay] = useState(false);
  const [companiesSundayHours, setCompaniesSundayHours] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      if (!companyId) {
        setTimeCut(0);
        setBreaks(DEFAULT_BREAKS);
        setCompaniesPdns([]);
        setCompaniesSundayHours([]);
        setIsWaiting(false);
        return;
      }

      setIsWaiting(true);

      try {
        const timeCutRes = await fetchTimeCut(companyId);
        if (timeCutRes && typeof timeCutRes.timeCut !== "undefined") {
          setTimeCut(timeCutRes.timeCut);
        }

        const breaksRes = await fetchBreaks(companyId);
        if (breaksRes && breaksRes.breaks) {
          setBreaks((prev) =>
            prev.map((b) => ({
              ...b,
              checked: b.name === breaksRes.breaks,
            }))
          );
        }

        const pdnsDisplayRes = await fetchPdnsDisplay(companyId);
        if (pdnsDisplayRes && typeof pdnsDisplayRes.isPdnsDisplay !== "undefined") {
          setIsPdnsDisplay(pdnsDisplayRes.isPdnsDisplay);
        }

        if (pdnsDisplayRes?.isPdnsDisplay) {
          const companiesPdnsRes = await fetchCompaniesPdns(companyId);
          if (companiesPdnsRes && companiesPdnsRes.companies) {
            setCompaniesPdns(companiesPdnsRes.companies);
          }
        } else {
          setCompaniesPdns([]);
        }

        const sundayHoursDisplayRes = await fetchSundayHoursDisplay(companyId);
        if (sundayHoursDisplayRes && typeof sundayHoursDisplayRes.isSundayHoursDisplay !== "undefined") {
          setIsSundayHoursDisplay(sundayHoursDisplayRes.isSundayHoursDisplay);
        }

        if (sundayHoursDisplayRes?.isSundayHoursDisplay) {
          const companiesSundayHoursRes = await fetchCompaniesSundayHours(companyId);
          if (companiesSundayHoursRes && companiesSundayHoursRes.companies) {
            setCompaniesSundayHours(companiesSundayHoursRes.companies);
          }
        } else {
          setCompaniesSundayHours([]);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des paramètres :", error);
      } finally {
        setIsWaiting(false);
      }
    };

    loadData();
  }, [companyId]);

  const commitTimeCut = useCallback(async (value) => {
    if (!companyId) return;
    const raw =
      value === "" || value === undefined || value === null
        ? ""
        : String(value).trim();
    const normalized = raw === "" ? 0 : raw;
    try {
      await updateTimeCut(normalized, companyId);
      setTimeCut(normalized);
    } catch (error) {
      console.error("Erreur lors de la mise à jour du timeCut :", error);
      throw error;
    }
  }, [companyId]);

  const commitBreaks = useCallback(async (selectedBreak) => {
    if (!selectedBreak || !companyId) return;
    try {
      await updateBreaks(selectedBreak, companyId);
      setBreaks((prev) =>
        prev.map((b) => ({
          ...b,
          checked: b.name === selectedBreak,
        })),
      );
    } catch (error) {
      console.error("Erreur lors de la mise à jour des pauses :", error);
      throw error;
    }
  }, [companyId]);

  const commitPdns = useCallback(async (isPdns) => {
    if (!companyId) return;
    setCompaniesPdns((prev) =>
      updateSelectedCompany(prev, companyId, "isPdns", isPdns),
    );
    try {
      await updateCompanyPdns(companyId, isPdns);
    } catch (error) {
      console.error("Erreur lors de la mise à jour PDNS :", error);
      setCompaniesPdns((prev) =>
        updateSelectedCompany(prev, companyId, "isPdns", !isPdns),
      );
      throw error;
    }
  }, [companyId]);

  const commitSundayHours = useCallback(async (isSundayHours) => {
    if (!companyId) return;
    setCompaniesSundayHours((prev) =>
      updateSelectedCompany(prev, companyId, "isSundayHours", isSundayHours),
    );
    try {
      await updateCompanySundayHours(companyId, isSundayHours);
    } catch (error) {
      console.error("Erreur lors de la mise à jour Heures Dimanche :", error);
      setCompaniesSundayHours((prev) =>
        updateSelectedCompany(prev, companyId, "isSundayHours", !isSundayHours),
      );
      throw error;
    }
  }, [companyId]);

  return {
    timeCut,
    breaks,
    isWaiting,
    commitTimeCut,
    commitBreaks,
    isPdnsDisplay,
    companiesPdns,
    commitPdns,
    isSundayHoursDisplay,
    companiesSundayHours,
    commitSundayHours,
  };
};

export default useSettingsTimeManagement;
